import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Pencil, 
  HardHat, 
  Shield, 
  Users, 
  Truck, 
  Calculator,
  FileText,
  BookOpen,
  ListChecks,
  ClipboardList,
  ArrowLeft,
  ExternalLink,
  Building2
} from 'lucide-react';

const API_URL = '';

// Icon mapping
const iconMap = {
  Pencil: Pencil,
  HardHat: HardHat,
  Shield: Shield,
  Users: Users,
  Truck: Truck,
  Calculator: Calculator,
  FileText: FileText,
  BookOpen: BookOpen,
  ListChecks: ListChecks,
  ClipboardList: ClipboardList,
};

// Color mapping for departments
const colorMap = {
  primary: 'bg-primary/20 text-primary border-primary/30 hover:bg-primary/30',
  orange: 'bg-orange-500/20 text-orange-500 border-orange-500/30 hover:bg-orange-500/30',
  purple: 'bg-purple-500/20 text-purple-500 border-purple-500/30 hover:bg-purple-500/30',
  pink: 'bg-pink-500/20 text-pink-500 border-pink-500/30 hover:bg-pink-500/30',
  cyan: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30 hover:bg-cyan-500/30',
  emerald: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/30',
};

// Policy type colors
const policyColors = {
  memos: 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30',
  policies: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/30',
  guidelines: 'bg-teal-500/20 text-teal-400 border-teal-500/30 hover:bg-teal-500/30',
  protocols: 'bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30',
};

export default function DepartmentPoliciesNav() {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentPolicies, setDepartmentPolicies] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/department-policies/departments`);
      setDepartments(res.data);
    } catch (error) {
      console.error('Error loading departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentPolicies = async (deptId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/department-policies/${deptId}`);
      setDepartmentPolicies(res.data);
      setSelectedDepartment(res.data.department);
    } catch (error) {
      console.error('Error loading department policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentClick = (dept) => {
    loadDepartmentPolicies(dept.id);
  };

  const handleBackClick = () => {
    setSelectedDepartment(null);
    setDepartmentPolicies(null);
  };

  const handlePolicyClick = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('No link available for this policy yet');
    }
  };

  if (loading && departments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show department policies
  if (selectedDepartment && departmentPolicies) {
    const DeptIcon = iconMap[selectedDepartment.icon] || Building2;
    
    return (
      <div className="space-y-6">
        {/* Back button and header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackClick}
            className="flex items-center gap-2"
            data-testid="btn-back-to-departments"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-md ${colorMap[selectedDepartment.color] || colorMap.primary}`}>
              <DeptIcon className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">{selectedDepartment.name}</h2>
          </div>
        </div>

        {/* Policy type buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {departmentPolicies.policy_links.map((policyLink) => {
            const PolicyIcon = iconMap[policyLink.policy_type.icon] || FileText;
            const hasLink = !!policyLink.link;
            
            return (
              <button
                key={policyLink.policy_type.id}
                onClick={() => handlePolicyClick(policyLink.link)}
                disabled={!hasLink}
                className={`p-6 rounded-lg border-2 transition-all duration-200 text-left group ${
                  hasLink 
                    ? policyColors[policyLink.policy_type.id] + ' cursor-pointer' 
                    : 'bg-muted/10 text-muted-foreground border-border/30 cursor-not-allowed opacity-60'
                }`}
                data-testid={`btn-policy-${policyLink.policy_type.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 mb-3">
                    <PolicyIcon className="w-6 h-6" />
                    <span className="text-lg font-semibold">{policyLink.policy_type.name}</span>
                  </div>
                  {hasLink && (
                    <ExternalLink className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity" />
                  )}
                </div>
                <p className="text-sm opacity-80">{policyLink.policy_type.description}</p>
                {policyLink.updated_at && (
                  <p className="text-xs opacity-60 mt-2">
                    Updated: {new Date(policyLink.updated_at).toLocaleDateString()}
                  </p>
                )}
                {!hasLink && (
                  <p className="text-xs mt-2 italic">Link not yet configured</p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Show department selection
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {departments.map((dept) => {
        const DeptIcon = iconMap[dept.icon] || Building2;
        
        return (
          <button
            key={dept.id}
            onClick={() => handleDepartmentClick(dept)}
            className={`p-6 rounded-lg border-2 transition-all duration-200 hover-lift ${colorMap[dept.color] || colorMap.primary}`}
            data-testid={`btn-dept-${dept.id}`}
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className="p-3 rounded-full bg-current/10">
                <DeptIcon className="w-8 h-8" />
              </div>
              <span className="text-lg font-semibold">{dept.name}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
